// ApiKeys.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { cleanString, downloadFile } from '../apiUtils';

// Services
import {
    deleteBaseServiceAccount,
    listBaseServiceAccounts,
    revokeBaseServiceAccount,
    rotateBaseServiceAccountToken,
} from 'services/api/ApiAccessService';

// CSS
import './ApiKeys.css';

// Components
import ApiKeyWizard from './ApiKeyWizard';
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import { toast } from 'react-toastify';

const API_KEY_WRITE_SCOPE_SET = new Set([
    'node.create',
    'node.updateOwn',
    'node.updateAny',
    'node.deleteOwn',
    'node.deleteAny',
    'collection.create',
    'collection.updateOwn',
    'collection.delete',
    'types.create',
    'types.fields.create',
    'types.update',
    'types.delete',
    'dashboard.write',
    'api.credentials.manage',
]);

function normalizeTypePermissionLevel(value) {
    const normalized = cleanString(value).toLowerCase();
    if (normalized === 'read' || normalized === 'write') {
        return normalized;
    }
    return 'none';
}

function resolveTypeRows(schemaTypes) {
    const rows = Array.isArray(schemaTypes) ? schemaTypes : [];

    return rows
        .map((row) => ({
            typeId: cleanString(row?.typeId || row?.id || row?.key).toLowerCase(),
            label: cleanString(row?.label || row?.name || row?.title),
        }))
        .filter((row) => row.typeId)
        .sort((a, b) => a.label.localeCompare(b.label));
}

function inferApiKeyAccessLevel(account) {
    const permissionMap = account?.permissions?.types && typeof account.permissions.types === 'object'
        ? account.permissions.types
        : {};

    const hasWritePermission = Object.values(permissionMap).some((level) => normalizeTypePermissionLevel(level) === 'write');
    if (hasWritePermission) return 'View and change data';

    const hasReadPermission = Object.values(permissionMap).some((level) => normalizeTypePermissionLevel(level) === 'read');
    if (hasReadPermission) return 'View only';

    const scopes = Array.isArray(account?.scopes) ? account.scopes : [];
    const hasWriteScope = scopes.some((scope) => API_KEY_WRITE_SCOPE_SET.has(cleanString(scope)));
    if (hasWriteScope) return 'View and change data';

    if (scopes.length > 0) return 'View only';

    return 'No data access';
}

function summarizeTypePermissions(typePermissionMap, schemaTypes) {
    const rows = resolveTypeRows(schemaTypes);
    const permissionMap = typePermissionMap && typeof typePermissionMap === 'object' ? typePermissionMap : {};

    let writeCount = 0;
    let readCount = 0;
    let noneCount = 0;

    rows.forEach((row) => {
        const level = normalizeTypePermissionLevel(permissionMap[row.typeId]);
        if (level === 'write') {
            writeCount += 1;
            return;
        }
        if (level === 'read') {
            readCount += 1;
            return;
        }
        noneCount += 1;
    });

    return {
        writeCount,
        readCount,
        noneCount,
        compact: `${writeCount} write • ${readCount} read • ${noneCount} none`,
    };
}

/**
 * ApiKeys
 *
 * Renders API key onboarding and creation flow for the API page.
 *
 * @param {string} baseId - Active base id.
 * @param {string} baseRole - Current user role in base.
 * @param {Array} schemaTypes - Base type rows.
 *
 * @returns {ReactNode} ApiKeys.
 */
const ApiKeys = React.memo(({
    baseId,
    baseRole,
    schemaTypes,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    const canManageCredentials = baseRole === 'owner' || baseRole === 'admin';

    // ----- MEMOS ----- //

    const typeRows = useMemo(() => resolveTypeRows(schemaTypes), [schemaTypes]);

    // ----- STATE ----- //

    const [loading, setLoading] = useState(false);
    const [serviceAccounts, setServiceAccounts] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [busyActionKey, setBusyActionKey] = useState('');

    const [apiKeyWizardOpen, setApiKeyWizardOpen] = useState(false);

    const [revealedToken, setRevealedToken] = useState('');
    const [revealedTokenAccountId, setRevealedTokenAccountId] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [pendingDeleteAccount, setPendingDeleteAccount] = useState(null);

    // ----- MEMOS ----- //

    const sortedServiceAccounts = useMemo(() => {
        return [...serviceAccounts].sort((a, b) => {
            const aDate = new Date(a?.lastUsedAt || a?.createdAt || 0).getTime();
            const bDate = new Date(b?.lastUsedAt || b?.createdAt || 0).getTime();
            return bDate - aDate;
        });
    }, [serviceAccounts]);

    const deleteConfirmMessage = useMemo(() => {
        const accountName = cleanString(pendingDeleteAccount?.name) || 'this API key';
        const status = cleanString(pendingDeleteAccount?.status || 'active').toLowerCase();

        if (status === 'revoked') {
            return `Delete revoked API key "${accountName}" permanently? This cannot be undone.`;
        }

        return `Delete API key "${accountName}" permanently? This immediately removes it and cannot be undone.`;
    }, [pendingDeleteAccount]);

    const loadApiKeys = useCallback(async () => {
        if (!baseId) return;

        setLoading(true);
        setErrorMessage('');

        try {
            if (!canManageCredentials) {
                setServiceAccounts([]);
                return;
            }

            const response = await listBaseServiceAccounts({ baseId });
            const rows = Array.isArray(response?.data) ? response.data : [];
            setServiceAccounts(rows);
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to load API keys.');
        } finally {
            setLoading(false);
        }
    }, [canManageCredentials, baseId]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        loadApiKeys();
    }, [loadApiKeys]);

    // ----- MEMOS ----- //

    const setTokenReveal = useCallback((serviceAccountId, token) => {
        const cleanToken = cleanString(token);
        if (!cleanToken) return;

        setRevealedToken(cleanToken);
        setRevealedTokenAccountId(cleanString(serviceAccountId));
    }, []);

    // ----- HANDLERS ----- //

    /**
 * Handles start api key flow.
 */

    const handleStartApiKeyFlow = useCallback(() => {
        if (!canManageCredentials) return;
        setErrorMessage('');
        setStatusMessage('');
        setApiKeyWizardOpen(true);
    }, [canManageCredentials]);

    /**
 * Handles api key wizard created.
 */
    const handleApiKeyWizardCreated = useCallback(async () => {
        await loadApiKeys();
    }, [loadApiKeys]);

    /**
 * Handles dismiss revealed token.
 */
    const handleDismissRevealedToken = useCallback(() => {
        setRevealedToken('');
        setRevealedTokenAccountId('');
    }, []);

    /**
 * Handles rotate api key.
 *
 * @param {*} serviceAccountId
 */
    const handleRotateApiKey = useCallback(async (serviceAccountId) => {
        if (!baseId || !serviceAccountId || !canManageCredentials) return;
        if (!window.confirm('Rotate this API key now? Existing integrations must update to the new key.')) return;

        setBusyActionKey(`rotate:${serviceAccountId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            const response = await rotateBaseServiceAccountToken({ baseId, serviceAccountId });
            const token = cleanString(response?.token);
            setTokenReveal(serviceAccountId, token);

            setStatusMessage('API key rotated. Save the new key before leaving this screen.');
            toast.success('API key rotated.');
            await loadApiKeys();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to rotate API key.');
        } finally {
            setBusyActionKey('');
        }
    }, [canManageCredentials, loadApiKeys, setTokenReveal, baseId]);

    /**
 * Handles revoke api key.
 *
 * @param {*} serviceAccountId
 */
    const handleRevokeApiKey = useCallback(async (serviceAccountId) => {
        if (!baseId || !serviceAccountId || !canManageCredentials) return;
        if (!window.confirm('Revoke this API key? This immediately blocks API access for this key.')) return;

        setBusyActionKey(`revoke:${serviceAccountId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            await revokeBaseServiceAccount({ baseId, serviceAccountId });
            if (cleanString(revealedTokenAccountId) === cleanString(serviceAccountId)) {
                setRevealedToken('');
                setRevealedTokenAccountId('');
            }
            setStatusMessage('API key revoked.');
            toast.success('API key revoked.');
            await loadApiKeys();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to revoke API key.');
        } finally {
            setBusyActionKey('');
        }
    }, [canManageCredentials, loadApiKeys, revealedTokenAccountId, baseId]);

    /**
 * Handles delete api key.
 *
 * @param {*} serviceAccountId
 * @param {*} status
 * @param {*} name
 */
    const handleDeleteApiKey = useCallback((serviceAccountId, status, name) => {
        if (!baseId || !serviceAccountId || !canManageCredentials) return;

        setPendingDeleteAccount({
            serviceAccountId: cleanString(serviceAccountId),
            status: cleanString(status || 'active').toLowerCase(),
            name: cleanString(name || serviceAccountId),
        });
        setDeleteConfirmOpen(true);
    }, [canManageCredentials, baseId]);

    /**
 * Handles set delete confirm open.
 *
 * @param {*} nextOpen
 */
    const handleSetDeleteConfirmOpen = useCallback((nextOpen) => {
        setDeleteConfirmOpen(Boolean(nextOpen));
        if (!nextOpen) {
            setPendingDeleteAccount(null);
        }
    }, []);

    /**
 * Handles confirm delete api key.
 */
    const handleConfirmDeleteApiKey = useCallback(async () => {
        const serviceAccountId = cleanString(pendingDeleteAccount?.serviceAccountId);
        if (!baseId || !serviceAccountId || !canManageCredentials) return;

        setDeleteConfirmOpen(false);

        setBusyActionKey(`delete:${serviceAccountId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            await deleteBaseServiceAccount({ baseId, serviceAccountId });
            if (cleanString(revealedTokenAccountId) === cleanString(serviceAccountId)) {
                setRevealedToken('');
                setRevealedTokenAccountId('');
            }
            setStatusMessage('API key deleted.');
            toast.success('API key deleted.');
            await loadApiKeys();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to delete API key.');
        } finally {
            setBusyActionKey('');
            setPendingDeleteAccount(null);
        }
    }, [canManageCredentials, loadApiKeys, pendingDeleteAccount, revealedTokenAccountId, baseId]);

    /**
 * Handles copy revealed token.
 */
    const handleCopyRevealedToken = useCallback(async () => {
        if (!revealedToken) return;

        try {
            await navigator.clipboard.writeText(revealedToken);
            toast.success('API key copied.');
        } catch (error) {
            toast.error(error?.message || 'Could not copy API key.');
        }
    }, [revealedToken]);

    /**
 * Handles download revealed token.
 */
    const handleDownloadRevealedToken = useCallback(() => {
        if (!revealedToken || !revealedTokenAccountId) return;

        const fileName = `base-${baseId}-api-key-${revealedTokenAccountId}.txt`;
        downloadFile(fileName, `${revealedToken}\n`, 'text/plain;charset=utf-8');
    }, [revealedToken, revealedTokenAccountId, baseId]);
    if (!baseId) {

        return (
            <div
              className="api-keys-note"
            >
                Select an active base to manage API keys.
            </div>
        );
    }

    // ----- RENDER ----- //

    return (
        <div className="api-keys-container">
            {!canManageCredentials && (
                <div
                  className="api-keys-note"
                >
                    Read-only mode for this user. Owners and admins can create and manage API keys.
                </div>
            )}

            {/* ERROR */}
            {errorMessage && (

                <div className="api-keys-error">{errorMessage}</div>
            )}

            {/* STATUS */}
            {statusMessage && !errorMessage && (

                <div className="api-keys-status">{statusMessage}</div>
            )}

            {/* CARD */}
            <section
              className="api-keys-card"
            >

                {/* TITLE */}
                <div
                  className="api-keys-title"
                >API Keys</div>

                {/* SUBTITLE */}
                <div
                  className="api-keys-subtitle"
                >
                    Create one API key per integration and set access by type.
                </div>

                {/* SUBTITLE */}
                <div
                  className="api-keys-subtitle"
                >
                    Any type not granted stays blocked by default.
                </div>

                {/* ACTIONS */}
                <div className="api-keys-actions">
                    <Button
                      iconKey="person_add"
                      label="+ Add API Key"
                      enabled={canManageCredentials && !loading}
                      onClick={handleStartApiKeyFlow}
                    />
                </div>

                {/* LIST */}
                <div className="api-keys-list">

                    {/* EMPTY */}
                    {sortedServiceAccounts.length === 0 && (

                        <div
                          className="api-keys-empty"
                        >
                            {loading ? 'Loading API keys...' : 'No API keys yet.'}
                        </div>
                    )}

                    {sortedServiceAccounts.map((account) => {
                        const accountId = account?.id || account?.key;
                        const accountStatus = cleanString(account?.status || 'active').toLowerCase();
                        const accessLabel = inferApiKeyAccessLevel(account);
                        const isRevoked = accountStatus === 'revoked';
                        const summary = summarizeTypePermissions(account?.permissions?.types, typeRows);

                        return (

                            <div
                              className="api-keys-row"
                              key={accountId}
                            >

                                {/* ROW META */}
                                <div className="api-keys-row-meta">

                                    {/* ROW TITLE */}
                                    <div
                                      className="api-keys-row-title"
                                    >
                                        {account?.name || ''}
                                    </div>
                                    <div
                                      className="api-keys-row-subtitle"
                                    >
                                        access: {accessLabel} • status: {account?.status || 'active'}
                                    </div>
                                    <div
                                      className="api-keys-row-subtitle"
                                    >
                                        type permissions: {summary.compact}
                                    </div>
                                </div>

                                {/* ACTIONS */}
                                <div className="api-keys-actions">
                                    <Button
                                      label="Rotate"
                                      enabled={canManageCredentials && !isRevoked && busyActionKey !== `rotate:${accountId}`}
                                      onClick={() => handleRotateApiKey(accountId)}
                                    />

                                    <Button
                                      label="Revoke"
                                      enabled={canManageCredentials && !isRevoked && busyActionKey !== `revoke:${accountId}`}
                                      foregroundColor={theme.warning}
                                      borderColor={theme.warning}
                                      onClick={() => handleRevokeApiKey(accountId)}
                                    />

                                    <Button
                                      label="Delete"
                                      enabled={canManageCredentials && busyActionKey !== `delete:${accountId}`}
                                      height="30px"
                                      fontSize="12px"
                                      borderRadius="6px"
                                      foregroundColor={theme.warning}
                                      borderColor={theme.warning}
                                      onClick={() => handleDeleteApiKey(accountId, accountStatus, account?.name || '')}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

            </section>

            <ApiKeyWizard
              isOpen={apiKeyWizardOpen}
              setOpen={setApiKeyWizardOpen}
              baseId={baseId}
              canManageCredentials={canManageCredentials}
              typeRows={typeRows}
              onCreated={handleApiKeyWizardCreated}
            />

            <Confirm
              title="Delete API Key"
              message={deleteConfirmMessage}
              isOpen={deleteConfirmOpen}
              setOpen={handleSetDeleteConfirmOpen}
              onConfirm={handleConfirmDeleteApiKey}
            />

            {Boolean(revealedToken) && (

                <section
                  className="api-keys-card"
                >

                    {/* TITLE */}
                    <div
                      className="api-keys-title"
                    >Save your API key (shown once)</div>

                    {revealedToken ? (
                        <>

                            {/* TOKEN */}
                            <textarea
                              className="api-keys-token"
                              value={revealedToken}
                              readOnly
                            />

                            {/* ACTIONS */}
                            <div className="api-keys-actions">
                                <Button
                                  label="Copy API Key"
                                  iconKey="content_copy"
                                  onClick={handleCopyRevealedToken}
                                />

                                <Button
                                  label="Download API Key"
                                  iconKey="download"
                                  onClick={handleDownloadRevealedToken}
                                />
                            </div>

                            {/* SUBTITLE */}
                            <div
                              className="api-keys-subtitle"
                            >
                                You cannot view this full API key again later.
                            </div>

                            {/* ACTIONS */}
                            <div className="api-keys-actions">
                                <Button
                                  label="Done"
                                  enabled={true}
                                  onClick={handleDismissRevealedToken}
                                />
                            </div>
                        </>
                    ) : (

                        <div
                          className="api-keys-empty"
                        >
                            Could not reveal API key. Please try creating it again.
                        </div>
                    )}
                </section>
            )}
        </div>
    );
});

// Dev Tools
ApiKeys.displayName = 'ApiKeys';

export default ApiKeys;
